import { createElement, formatUnit } from '@syncfusion/ej2-base';
import { events } from '../../../common/constant';
import * as constants from '../../../common/constant';
import { decoupleReference, extractBlockTypeFromElement, getBlockModelById, getColgroupChildren, setTableWidthMode } from '../../../common/utils/index';
import { TableUIManager } from '../../plugins/table/ui-manager';
export class TableRenderer {
    constructor(editor) {
        this.uiManagers = new Map();
        this.nonEditableElements = [
            'e-row-insert-handle',
            'e-col-insert-handle',
            'e-row-action-handle',
            'e-col-action-handle',
            'e-row-dot',
            'e-col-dot',
            'e-row-dot-hit',
            'e-col-dot-hit',
            'e-row-number'
        ];
        this.parent = editor;
        this.addEventListener();
    }
    addEventListener() {
        this.parent.observer.on('input', this.handleHeaderInput, this);
        this.parent.observer.on(events.destroy, this.destroy, this);
    }
    removeEventListener() {
        this.parent.observer.off('input', this.handleHeaderInput);
        this.parent.observer.off(events.destroy, this.destroy);
    }
    /**
     * Renders the table block element in the editor
     *
     * @param {BlockModel} block - The data model of the block, used to determine available actions and state.
     * @param {HTMLElement} blockElement - The root DOM element representing the table block.
     * @returns {HTMLElement} - The table container element
     * @hidden
     */
    renderTable(block, blockElement) {
        blockElement.classList.add(constants.TABLE_BLOCK_CLS);
        const props = block.properties;
        const blockId = block.id;
        const tableContainer = createElement('div', {
            className: 'e-table-container e-scrollable-block'
        });
        const table = createElement('table', {
            className: 'e-table-element',
            attrs: {
                'data-block-id': blockId,
                'data-col-counter': `${(props.columns).length}`,
                'role': 'grid',
                'style': `width: ${formatUnit(props.width)}`
            }
        });
        tableContainer.appendChild(table);
        // Create colgroup for column width management
        const colgroup = createElement('colgroup');
        table.appendChild(colgroup);
        if (props.enableRowNumbers) {
            const rnCol = createElement('col');
            rnCol.classList.add('e-col-row-number');
            rnCol.style.width = '44px'; // fixed width for row numbers
            colgroup.appendChild(rnCol);
        }
        const colCount = props.columns.length;
        const newWidth = (100 / colCount).toLocaleString() + '%';
        // Add columns with specified widths or default
        for (let i = 0; i < colCount; i++) {
            const col = createElement('col');
            col.style.width = props.columns[i].width ? formatUnit(props.columns[i].width) : newWidth;
            colgroup.appendChild(col);
        }
        if (props.enableHeader && colCount > 0) {
            const thead = createElement('thead');
            const headerRow = createElement('tr');
            if (props.enableRowNumbers) {
                const thRN = createElement('th', {
                    className: 'e-row-number',
                    attrs: { 'aria-hidden': 'true', tabindex: '-1', contenteditable: 'false' }
                });
                headerRow.appendChild(thRN);
            }
            props.columns.forEach((c, cIdx) => {
                const th = createElement('th');
                th.textContent = c.headerText;
                th.dataset.row = '0';
                th.dataset.col = cIdx.toString();
                th.tabIndex = 0;
                th.setAttribute('role', 'columnheader');
                th.setAttribute('title', c.headerText);
                th.setAttribute('contenteditable', 'true');
                headerRow.appendChild(th);
            });
            thead.appendChild(headerRow);
            table.appendChild(thead);
        }
        const tbody = createElement('tbody');
        props.rows.forEach((row, rIdx) => {
            const visualIndex = props.enableHeader ? rIdx + 1 : rIdx;
            tbody.appendChild(this.parent.tableService.createRow(visualIndex, props, block, row));
        });
        table.appendChild(tbody);
        if (!props.readOnly) {
            this.attachHoverUI(table, blockElement, block);
            this.parent.tableSelectionManager.attachTableEvents(table, blockElement);
        }
        else {
            this.updateTableReadyOnlyState(table, props.readOnly);
        }
        return tableContainer;
    }
    /**
     * Refreshes the column width and it's mode based on available container width
     *
     * @param {BlockModel} block - The data model of the block.
     * @returns {void}
     * @hidden
     */
    refreshColWidths(block) {
        const blockElement = this.parent.getBlockElementById(block.id);
        const props = block.properties;
        const colCount = props.columns.length;
        const table = blockElement.querySelector('table');
        // === NEW: Decide width mode on initial render ===
        let usePxMode = false;
        let defaultWidthsPx = [];
        if (colCount > 0) {
            const containerWidth = table.clientWidth;
            // Check if equal % would violate min width
            const projectedPx = containerWidth / colCount;
            if (projectedPx < constants.TABLE_NEW_COL_WIDTH) {
                usePxMode = true;
            }
            // Pre-compute px widths if switching to px mode
            if (usePxMode) {
                const equalPx = Math.max(constants.TABLE_NEW_COL_WIDTH, Math.floor(containerWidth / colCount));
                defaultWidthsPx = Array(colCount).fill(equalPx);
                setTableWidthMode(table, 'px');
            }
        }
        const dataCols = getColgroupChildren(table);
        // Apply columns with correct width mode
        dataCols.forEach((colEl, i) => {
            // Respect the user provided width if any
            if (props.columns[i].width) {
                return;
            }
            if (usePxMode) {
                const widthPx = defaultWidthsPx[i];
                colEl.style.width = `${widthPx}px`;
                props.columns[i].width = `${widthPx}px`; // sync model
            }
            else {
                const pct = (100 / colCount).toFixed(2) + '%';
                colEl.style.width = pct;
                props.columns[i].width = pct;
            }
        });
    }
    /**
     * Attaches unified hover UI elements (dots, hit-zones, drag lines, action menu, and insert handles)
     * to the specified block when the mouse hovers over it
     *
     * @param {HTMLTableElement} table - The table element associated with the block (used for certain block types).
     * @param {HTMLElement} blockElement - The root DOM element representing the table block.
     * @param {BlockModel} blockModel - The data model of the block, used to determine available actions and state.
     * @returns {void}
     * @hidden
     */
    attachHoverUI(table, blockElement, blockModel) {
        const uiManager = this.registerUIManager(blockElement.id);
        uiManager.init(table, blockElement, blockModel);
    }
    /**
     * Removes the created ui manager instance for the particular block
     *
     * @param {string} blockId - The id of the table block
     * @returns {void}
     * @hidden
     */
    removeHoverUI(blockId) {
        const uiManager = this.getManager(blockId);
        if (uiManager) {
            uiManager.destroy();
            this.uiManagers.delete(blockId);
        }
    }
    /**
     * Registers the UI manager instance in map for retrieving.
     *
     * @param {string} blockId - The id of the table block
     * @returns {TableUIManager} - Manager instance
     * @hidden
     */
    registerUIManager(blockId) {
        const manager = new TableUIManager(this.parent);
        this.uiManagers.set(blockId, manager);
        return manager;
    }
    /**
     * Fetches the UI manager instance based on blockId.
     *
     * @param {string} blockId - The id of the table block
     * @returns {TableUIManager} - Manager instance
     * @hidden
     */
    getManager(blockId) {
        return this.uiManagers.get(blockId);
    }
    /**
     * Updates the read-only state of a table block by toggling content editing capabilities.
     *
     * @param {HTMLTableElement} element - The table element to update.
     * @param {boolean} value - `true` to make the table read-only, `false` to make it editable.
     * @returns {void}
     * @hidden
     */
    updateTableReadyOnlyState(element, value) {
        let editableElements = Array.from(element.querySelectorAll(`[contenteditable='${value}']`));
        editableElements = editableElements.filter((element) => {
            return !this.nonEditableElements.some((className) => element.classList.contains(className));
        });
        editableElements.forEach((element) => {
            element.contentEditable = (!value).toString();
            element.dataset.tableReadonlyProcessed = 'true';
        });
        element.classList.toggle('e-readonly', value);
    }
    /**
     * Based on current focused cell element, resolves the context and returns it.
     *
     * @returns {TableContext} - The context object containing details about current focused table
     * @hidden
     */
    resolveTableContext() {
        const focusedEl = this.parent.currentFocusedBlock;
        const tableBlockEl = focusedEl && focusedEl.closest('.' + constants.TABLE_BLOCK_CLS);
        if (!tableBlockEl) {
            return null;
        }
        const tableEl = tableBlockEl.querySelector('table');
        const blockId = tableEl.getAttribute('data-block-id') || tableBlockEl.id;
        const tableBlock = getBlockModelById(blockId, this.parent.getEditorBlocks());
        const props = tableBlock.properties;
        const td = focusedEl.closest('td') || tableEl.querySelector('.e-cell-focus');
        const startDataRow = props.enableHeader ? (parseInt(td.dataset.row, 10) - 1) : parseInt(td.dataset.row, 10);
        const startDataCol = parseInt(td.dataset.col, 10);
        return { tableBlockEl, tableEl, props, startDataRow, startDataCol };
    }
    handleHeaderInput() {
        const blockElement = this.parent.currentFocusedBlock;
        if (!blockElement || (blockElement && extractBlockTypeFromElement(blockElement) !== 'Table')) {
            return;
        }
        const blockModel = getBlockModelById(blockElement.id, this.parent.getEditorBlocks());
        const props = blockModel.properties;
        const tableHeaders = blockElement.querySelectorAll('thead th:not(.e-row-number)');
        const oldColumns = decoupleReference(props.columns);
        tableHeaders.forEach((headerElement) => {
            const colIndex = parseInt(headerElement.getAttribute('data-col'), 10);
            const updatedHeaderText = headerElement.textContent;
            props.columns[colIndex].headerText = updatedHeaderText;
        });
        const updatedColumns = decoupleReference(props.columns);
        this.parent.undoRedoAction.trackTableHeaderInputForUndoRedo({
            blockId: blockModel.id,
            oldColumns,
            updatedColumns
        });
    }
    destroyAllTableManagers() {
        if (this.uiManagers.size > 0) {
            this.uiManagers.forEach((manager) => {
                manager.destroy();
            });
            this.uiManagers.clear();
        }
    }
    destroy() {
        this.removeEventListener();
        this.destroyAllTableManagers();
    }
}
